<template>
  <UIButtonIcon
    v-if="shrink"
    :leading="MagnifyingGlassIcon"
    variant="gray"
    fill="light"
    :size
    @click="isOpen = true"
  >
    <template
      v-if="!!modelStorage?.trim()"
      #after
    >
      <span class="ui-panel-search__dotted">...</span>
    </template>
  </UIButtonIcon>
  <div
    v-if="isSearchOpen"
    :class="searchClasses"
  >
    <UIInputTextClearable
      v-tooltip="tooltip"
      :model-value="modelStorage"
      :size
      :disabled
      :placeholder
      v-bind="$attrs"
      @ready="handleReady"
      @update:model-value="handleUpdateModelValue"
    >
      <template #leading>
        <MagnifyingGlassIcon aria-hidden="true" />
      </template>
    </UIInputTextClearable>
    <UIButtonIcon
      v-if="isOpen && shrink"
      :leading="XMarkIcon"
      type="button"
      variant="gray"
      fill="outlined"
      :size
      @click="isOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { ButtonSize } from '../Button/utils/types'

import { DEBOUNCE_DELAY } from '@/const'

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'

import { UIInputTextClearable } from '@ui/fields'
import { useDebounceFn, useTimeout } from '@vueuse/core'
import { UIButtonIcon } from '@ui/buttons'

type Props = {
  minLength?: number
  size?: ButtonSize
  shrink?: boolean
  disabled?: boolean
  placeholder?: string
  debounce?: boolean
}

const { minLength = 1, shrink, debounce } = defineProps<Props>()

const modelValue = defineModel<string>()
const modelStorage = defineModel<string>('storage', { default: '' })

const { isPending, start, stop } = useTimeout(1000, {
  controls: true,
  immediate: false,
})
stop()

const isOpen = ref(false)

const cleanedValue = computed(() => modelStorage.value?.trim() || '')

const isTooltipShown = computed(() => !isPending.value && minLength > 1 && !!cleanedValue.value && cleanedValue.value.length < minLength)

const tooltip = computed(() => ({
  content: `Minimum ${minLength} characters`,
  shown: isTooltipShown.value,
  placement: 'top',
  triggers: ['manual'],
}))

const isSearchOpen = computed(() => !shrink || isOpen.value)

const searchClasses = computed(() => ({
  'ui-panel-search--open': isOpen.value,
}))

const handleUpdateModelValue = useDebounceFn((value: string) => {
  modelStorage.value = value
}, debounce ? DEBOUNCE_DELAY * 2 : 0)

const handleReady = (el: HTMLInputElement) => {
  if (isOpen.value && shrink) {
    el.focus()
  }
}

watch(
  cleanedValue,
  value => {
    if (value) start()
    if (value.length < minLength) {
      modelValue.value = undefined
    } else {
      stop()
      modelValue.value = value || undefined
    }
  },
  { immediate: true },
)
</script>

<style>
.ui-panel-search {
  &__dotted {
    @apply w-3.5 aspect-square;
    @apply absolute top-1 right-1;
    @apply flex items-center justify-center;
    @apply text-[10px] leading-none;
    @apply bg-primary text-white;
    @apply rounded-full;
  }
  &--open {
    @apply absolute inset-0;
    @apply flex items-center gap-2;
    @apply bg-body;
    @apply z-10;

    .ui-input-text {
      @apply flex-auto;
    }
  }
}
</style>
